import { Component } from '@theme/component';

/**
 * A horizontal scroll-snap carousel for the shoppable video list on mobile.
 *
 * Adds previous/next chevrons and optional auto-rotate. It is inert on desktop, where the list is a
 * static grid rather than an overflowing scroller — `#scrollable` guards both the arrows and the
 * autoplay tick so nothing happens when there is nothing to scroll.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The horizontally scrolling grid.
 * @property {HTMLButtonElement} [previous] - The previous chevron.
 * @property {HTMLButtonElement} [next] - The next chevron.
 *
 * @extends {Component<Refs>}
 */
class ShoppableVideoListComponent extends Component {
  requiredRefs = ['scroller'];

  /**
   * The interval ID for auto-rotate.
   * @type {number | undefined}
   */
  #interval;

  connectedCallback() {
    super.connectedCallback();

    // Pause auto-rotate while the visitor is interacting or reading.
    this.addEventListener('pointerenter', this.#suspend);
    this.addEventListener('pointerleave', this.#resume);
    this.refs.scroller.addEventListener('pointerdown', this.#suspend);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#play();
  }

  disconnectedCallback() {
    this.#suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    super.disconnectedCallback();
  }

  /**
   * The rendered cards, ignoring zero-width nodes such as the `<script>` tags each block emits.
   * @returns {HTMLElement[]}
   */
  get #cards() {
    return Array.from(this.refs.scroller.children).filter(
      (element) => element instanceof HTMLElement && element.getBoundingClientRect().width > 0
    );
  }

  /**
   * Whether the scroller can actually scroll. False on desktop, where the grid is not overflowing.
   * @returns {boolean}
   */
  get #scrollable() {
    const { scroller } = this.refs;
    return scroller.scrollWidth - scroller.clientWidth > 2;
  }

  /**
   * The scroll offset that aligns a card to the scroller's start edge.
   * @param {HTMLElement} card
   * @returns {number}
   */
  #offsetOf(card) {
    const { scroller } = this.refs;
    return card.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
  }

  /**
   * Index of the card currently closest to the scroller's start edge.
   * @param {HTMLElement[]} cards
   * @returns {number}
   */
  #currentIndex(cards) {
    const current = this.refs.scroller.scrollLeft;
    let closest = 0;
    let smallest = Infinity;

    cards.forEach((card, index) => {
      const distance = Math.abs(this.#offsetOf(card) - current);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });

    return closest;
  }

  /**
   * Scrolls to a card by index. Adjacent moves animate smoothly; a wrap-around (past either end)
   * jumps instantly, so it doesn't smooth-scroll the whole width back — which reads as a fast blur.
   * @param {number} index
   */
  #scrollToCard(index) {
    const cards = this.#cards;
    if (cards.length < 2) return;

    const wrapped = ((index % cards.length) + cards.length) % cards.length;
    const behavior = index === wrapped ? 'smooth' : 'instant';

    this.refs.scroller.scrollTo({ left: this.#offsetOf(cards[wrapped]), behavior });
  }

  next = () => {
    if (!this.#scrollable) return;
    this.#scrollToCard(this.#currentIndex(this.#cards) + 1);
  };

  previous = () => {
    if (!this.#scrollable) return;
    this.#scrollToCard(this.#currentIndex(this.#cards) - 1);
  };

  /**
   * @returns {boolean} Whether auto-rotate is enabled.
   */
  get #autoplay() {
    return this.hasAttribute('autoplay');
  }

  /**
   * @returns {number} The auto-rotate interval in milliseconds.
   */
  get #autoplayInterval() {
    const value = parseInt(this.getAttribute('autoplay-speed') ?? '', 10);
    return Number.isNaN(value) ? 5000 : value * 1000;
  }

  #play = () => {
    if (!this.#autoplay) return;

    this.#suspend();

    this.#interval = setInterval(() => {
      if (document.hidden || this.matches(':hover') || !this.#scrollable) return;

      this.next();
    }, this.#autoplayInterval);
  };

  #suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  #resume = () => {
    if (this.#autoplay && this.#interval === undefined) this.#play();
  };

  #handleVisibilityChange = () => (document.hidden ? this.#suspend() : this.#resume());
}

if (!customElements.get('shoppable-video-list-component')) {
  customElements.define('shoppable-video-list-component', ShoppableVideoListComponent);
}
